import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Trash, FilePdf, Receipt, MagnifyingGlass, UserPlus, User,
  PaperPlaneTilt, CheckCircle, Clock, WarningCircle, ListChecks, ClockCounterClockwise, PencilSimple, ArrowsClockwise, Check, CaretDown, DotsSixVertical,
  TrendUp, TrendDown, ClipboardText, ArrowRight, FirstAidKit, CurrencyDollar, Wrench,
} from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth, hasRole } from "@/context/AuthContext";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { ProductPicker } from "@/components/sales/ProductPicker";
import { tokenMatch } from "@/lib/search";
import { DatePicker } from "@/components/sales/DatePicker";
import { SwapDialog } from "@/components/sales/SwapDialog";
import { TIERS, STATUS_META, PAYMENT_FILTERS, tierPrice, fmt, todayISO, toISO } from "@/components/sales/constants";
import AccessoryChips from "@/components/AccessoryChips";


export default function Sales() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [sales, setSales] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [repairOpen, setRepairOpen] = useState(false);
  const [isQuoteMode, setIsQuoteMode] = useState(false);
  const [quoteValidUntil, setQuoteValidUntil] = useState("");
  const [showDetail, setShowDetail] = useState(null);
  const [swapFor, setSwapFor] = useState(null);
  const [showClientForm, setShowClientForm] = useState(false);
  const [newClient, setNewClient] = useState({
    name: "", business_name: "", display_name: "", city: "", address: "",
    phone: "", currency: "MXN", client_type: "final", notes: "", crm_stage: "lead",
  });
  const [savingClient, setSavingClient] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [items, setItems] = useState([]);
  // Drag & drop reordering of sale line items
  const dragItemIdxRef = useRef(null);
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null); // {idx, pos: "above"|"below"}
  const [notes, setNotes] = useState("");
  const [extraTerms, setExtraTerms] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState(todayISO());
  const [discount, setDiscount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [showPayment, setShowPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: "", currency: "", bank_id: "", exchange_rate: "", note: "" });
  const [payingPayment, setPayingPayment] = useState(false);
  const paymentIdemKeyRef = useRef(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPayForm, setEditPayForm] = useState({ amount: "", currency: "", bank_id: "", exchange_rate: "", note: "", reason: "" });
  const [showDueEdit, setShowDueEdit] = useState(false);
  const [dueEditForm, setDueEditForm] = useState({ due_date: "", reason: "" });
  const [toDelete, setToDelete] = useState(null);
  const [repairQuoteFor, setRepairQuoteFor] = useState(null); // sale being repaired
  const [repairQuoteAmount, setRepairQuoteAmount] = useState("");
  const [savingRepairQuote, setSavingRepairQuote] = useState(false);
  const [editingSaleId, setEditingSaleId] = useState(null);
  const [editingSaleStatus, setEditingSaleStatus] = useState("draft");
  const [editReason, setEditReason] = useState("");
  const [sellers, setSellers] = useState([]);
  const [sellerId, setSellerId] = useState("");
  // Cliente display preference: "name" | "business" | "" (auto)
  const [clientDisplayAs, setClientDisplayAs] = useState("");

  // Elige el label del cliente respetando `client_display_as` guardado en la nota.
  const clientLabel = (sale) => {
    const c = sale.client_snapshot || {};
    const mode = sale.client_display_as;
    if (mode === "business" && c.business_name) return c.business_name;
    if (mode === "name" && c.name) return c.name;
    return c.display_name || c.name || c.business_name || "—";
  };
  const [changeSellerFor, setChangeSellerFor] = useState(null);
  const [changeSellerForm, setChangeSellerForm] = useState({ seller_user_id: "", reason: "" });
  const [savingSellerChange, setSavingSellerChange] = useState(false);

  const load = async () => {
    const [s, c, p, b, ss] = await Promise.all([
      api.get("/sales"), api.get("/clients"), api.get("/products"),
      api.get("/banks").catch(() => ({ data: [] })),
      api.get("/sales/sellers").catch(() => ({ data: [] })),
    ]);
    setSales(s.data); setClients(c.data); setProducts(p.data); setBanks(b.data); setSellers(ss.data);
  };
  useEffect(() => { load(); }, []);

  // Auto-open a specific sale via ?open=<id> in URL (used by Finance dashboard links)
  useEffect(() => {
    const openId = params.get("open");
    if (!openId) return;
    api.get(`/sales/${openId}`).then((r) => {
      setShowDetail(r.data);
      // clear param to avoid re-opening on subsequent refreshes
      const sp = new URLSearchParams(params);
      sp.delete("open");
      setParams(sp, { replace: true });
    }).catch(() => {});
  }, [params, setParams]);

  const openNew = (asQuote = false) => {
    setEditingSaleId(null);
    setEditingSaleStatus("draft");
    setEditReason("");
    setClientId(""); setItems([]); setNotes(""); setExtraTerms("");
    setInvoiceDate(todayISO()); setDueDate(todayISO()); setDiscount(0);
    setClientDisplayAs("");
    setIsQuoteMode(!!asQuote);
    // Default expiration: 15 days from today
    if (asQuote) {
      const d = new Date();
      d.setDate(d.getDate() + 15);
      setQuoteValidUntil(toISO(d));
    } else {
      setQuoteValidUntil("");
    }
    // Default seller to the current user (find their id in the sellers list)
    const me = sellers.find((s) => s.email === user?.email);
    setSellerId(me?.id || "");
    setShowForm(true);
  };

  const submitNewClient = async (e) => {
    e.preventDefault();
    setSavingClient(true);
    try {
      const { data } = await api.post("/clients", newClient);
      setClients((prev) => [data, ...prev]);
      setClientId(data.id);
      toast.success("Cliente creado y seleccionado");
      setShowClientForm(false);
      setNewClient({ name: "", business_name: "", display_name: "", city: "", address: "",
        phone: "", currency: "MXN", client_type: "final", notes: "", crm_stage: "lead" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSavingClient(false); }
  };

  const addItem = () => setItems((x) => [...x, { product_id: "", qty: 1, tier: "store", unit_price: 0, currency: "MXN", is_custom: false, custom_name: "", custom_description: "" }]);
  const addCustomItem = () => setItems((x) => [...x, { product_id: "", qty: 1, tier: "custom", unit_price: 0, currency: saleCurrency || "MXN", is_custom: true, custom_name: "", custom_description: "" }]);
  const removeItem = (i) => setItems((x) => x.filter((_, idx) => idx !== i));

  // ---- Drag & drop reordering ----
  const reorderItems = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx == null || toIdx == null) return;
    setItems((x) => {
      const next = [...x];
      const [moved] = next.splice(fromIdx, 1);
      // After removal, indices >= fromIdx shifted by -1
      const insertAt = toIdx > fromIdx ? toIdx - 1 : toIdx;
      next.splice(insertAt, 0, moved);
      return next;
    });
  };
  const onItemDragStart = (idx) => (e) => {
    dragItemIdxRef.current = idx;
    setDraggingIdx(idx);
    try { e.dataTransfer.effectAllowed = "move"; } catch { /* ignore */ }
  };
  const onItemDragOver = (idx) => (e) => {
    e.preventDefault();
    if (dragItemIdxRef.current == null || dragItemIdxRef.current === idx) return;
    const r = e.currentTarget.getBoundingClientRect();
    const pos = e.clientY < r.top + r.height / 2 ? "above" : "below";
    setDragOverIdx((p) => (p?.idx === idx && p?.pos === pos ? p : { idx, pos }));
    try { e.dataTransfer.dropEffect = "move"; } catch { /* ignore */ }
  };
  const onItemDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragItemIdxRef.current;
    const over = dragOverIdx;
    if (from != null && over) {
      const to = over.pos === "below" ? over.idx + 1 : over.idx;
      reorderItems(from, to);
    }
    dragItemIdxRef.current = null;
    setDraggingIdx(null);
    setDragOverIdx(null);
  };
  const onItemDragEnd = () => {
    dragItemIdxRef.current = null;
    setDraggingIdx(null);
    setDragOverIdx(null);
  };
  const updateItem = (i, patch) => {
    setItems((x) => x.map((it, idx) => {
      if (idx !== i) return it;
      const next = { ...it, ...patch };
      if (!it.is_custom && (patch.product_id || patch.tier)) {
        const prod = products.find((p) => p.id === (patch.product_id || it.product_id));
        if (prod && next.tier !== "custom") {
          next.unit_price = tierPrice(prod, next.tier);
          next.currency = prod.sale_currency || "MXN";
        }
        // Auto-populate the editable description from the catalog when the
        // seller picks a product. If the description hasn't been manually
        // edited yet (empty), we copy the product's default characteristics.
        if (patch.product_id && prod && !it.custom_description) {
          next.custom_description = prod.characteristics || "";
        }
      }
      return next;
    }));
  };

  const subtotal = useMemo(() => items.reduce((s, it) => s + (Number(it.unit_price) || 0) * (Number(it.qty) || 0), 0), [items]);
  const total = useMemo(() => Math.max(0, subtotal - (Number(discount) || 0)), [subtotal, discount]);
  const saleCurrency = useMemo(() => clients.find((x) => x.id === clientId)?.currency || "MXN", [clientId, clients]);

  const submit = async (e) => {
    e.preventDefault();
    if (!clientId) return toast.error("Selecciona un cliente");
    if (items.length === 0) return toast.error("Agrega al menos un producto");
    if (items.some((it) => it.is_custom ? !it.custom_name?.trim() || !it.qty : (!it.product_id || !it.qty))) {
      return toast.error("Completa todos los conceptos");
    }
    if (dueDate < todayISO()) return toast.error("La fecha de vencimiento no puede ser pasada");
    if (editingSaleId && editingSaleStatus !== "draft" && !editReason.trim()) {
      return toast.error("Captura una justificación para editar esta nota");
    }
    setSaving(true);
    try {
      const payload = {
        client_id: clientId,
        items: items.map((it) => ({
          product_id: it.is_custom ? null : it.product_id,
          custom_name: it.is_custom ? it.custom_name : null,
          // For inventory products this acts as an override for the printed
          // description on the note — it does NOT modify the catalog.
          custom_description: it.custom_description || "",
          qty: parseInt(it.qty), unit_price: parseFloat(it.unit_price),
          currency: it.currency, tier: it.tier || "custom",
        })),
        notes, extra_terms: extraTerms, invoice_date: invoiceDate, due_date: dueDate,
        discount: Number(discount) || 0,
        seller_user_id: sellerId || null,
        edit_reason: editingSaleId && editingSaleStatus !== "draft" ? editReason.trim() : undefined,
        is_quote: !editingSaleId && isQuoteMode,
        quote_valid_until: (!editingSaleId && isQuoteMode && quoteValidUntil) || undefined,
        client_display_as: clientDisplayAs || null,
      };
      let data;
      if (editingSaleId) {
        ({ data } = await api.put(`/sales/${editingSaleId}`, payload));
        toast.success(`${data.is_quote ? "Cotización" : "Nota"} #${String(data.folio).padStart(6, "0")} actualizada`);
      } else {
        ({ data } = await api.post("/sales", payload));
        toast.success(`${data.is_quote ? "Cotización" : "Nota"} #${String(data.folio).padStart(6, "0")} creada`);
      }
      setShowForm(false);
      setEditingSaleId(null);
      // Open the detail of the just-created/edited sale so the user can mark it Enviada with one click
      setShowDetail(data);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const downloadPdf = async (sale) => {
    try {
      const res = await api.get(`/sales/${sale.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${sale.is_quote ? "cotizacion" : "nota"}-${String(sale.folio).padStart(6, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error descargando PDF"); }
  };

  const markSent = async (sale) => {
    try {
      const { data } = await api.patch(`/sales/${sale.id}/status`, { status: "sent" });
      toast.success("Nota marcada como Enviada");
      setShowDetail(data); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const convertQuote = async (quote) => {
    if (!window.confirm(`¿Convertir cotización #${String(quote.folio).padStart(6, "0")} en nota de venta? Se descontará stock.`)) return;
    try {
      const { data } = await api.post(`/sales/${quote.id}/convert-to-sale`, { due_date: todayISO() });
      toast.success(`Nota #${String(data.folio).padStart(6, "0")} creada desde cotización`);
      setShowDetail(data);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const openEditSale = (sale) => {
    setEditingSaleId(sale.id);
    setEditingSaleStatus(sale.status || "draft");
    setEditReason("");
    setIsQuoteMode(!!sale.is_quote);
    setQuoteValidUntil(sale.quote_valid_until || "");
    setClientId(sale.client_id);
    setItems((sale.items || []).map((it) => ({
      product_id: it.product_id || "",
      is_custom: !!it.is_custom,
      custom_name: it.is_custom ? it.product_name : "",
      custom_description: it.characteristics || "",
      qty: it.qty,
      unit_price: it.unit_price,
      currency: it.currency,
      tier: it.tier || "custom",
    })));
    setNotes(sale.notes || "");
    setExtraTerms(sale.extra_terms || "");
    setInvoiceDate(sale.invoice_date || todayISO());
    setDueDate(sale.due_date || todayISO());
    setDiscount(Number(sale.discount || 0));
    setSellerId(sale.seller_user_id || sellers.find((s) => s.email === user?.email)?.id || "");
    setClientDisplayAs(sale.client_display_as || "");
    setShowDetail(null);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/sales/${toDelete.id}`);
      toast.success("Nota cancelada y stock restaurado");
      setShowDetail(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cancelar la nota");
    } finally {
      setToDelete(null);
    }
  };

  const restoreSale = async (sale) => {
    if (!confirm(`Restaurar la nota #${String(sale.folio).padStart(6, "0")}? Se descontará el stock de nuevo.`)) return;
    try {
      const { data } = await api.post(`/sales/${sale.id}/restore`);
      toast.success("Nota restaurada");
      setShowDetail(data.sale);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Error al restaurar la nota");
      toast.error(msg);
    }
  };

  const openPaymentDialog = (sale) => {
    setPaymentForm({
      amount: sale.outstanding.toFixed(2),
      currency: sale.currency,
      bank_id: "",
      exchange_rate: "",
      note: "",
    });
    // Generate a fresh idempotency key for this dialog open. Any double-click
    // will reuse this same key, so the backend dedupes the second request.
    paymentIdemKeyRef.current = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    setPayingPayment(false);
    setShowPayment(true);
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    if (!showDetail || payingPayment) return;
    const sale = showDetail;
    const amt = parseFloat(paymentForm.amount);
    if (!amt || amt <= 0) return toast.error("Monto inválido");
    if (!paymentForm.bank_id) return toast.error("Selecciona un banco");
    const isDiffCurrency = paymentForm.currency !== sale.currency;
    if (isDiffCurrency && !paymentForm.exchange_rate) return toast.error("Falta el tipo de cambio");
    setPayingPayment(true);
    try {
      const { data } = await api.post(`/sales/${sale.id}/payments`, {
        amount: amt, currency: paymentForm.currency, bank_id: paymentForm.bank_id,
        exchange_rate: isDiffCurrency ? parseFloat(paymentForm.exchange_rate) : null,
        note: paymentForm.note,
        idempotency_key: paymentIdemKeyRef.current,
      });
      toast.success(`Pago registrado · saldo: ${fmt(data.outstanding, sale.currency)}`);
      setShowDetail(data); setShowPayment(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setPayingPayment(false); }
  };

  const removePayment = async (pid, reason) => {
    if (!showDetail) return;
    try {
      const res = await api.delete(`/sales/${showDetail.id}/payments/${pid}`, { params: { reason } });
      toast.success("Pago revertido");
      setShowDetail(res.data); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const openEditPayment = (p) => {
    setEditingPayment(p);
    setEditPayForm({
      amount: String(p.amount),
      currency: p.currency,
      bank_id: p.bank_id,
      exchange_rate: p.exchange_rate ? String(p.exchange_rate) : "",
      note: p.note || "",
      reason: "",
    });
  };

  const submitEditPayment = async (e) => {
    e.preventDefault();
    if (!showDetail || !editingPayment) return;
    if (!editPayForm.reason.trim()) return toast.error("Indica el motivo de la edición");
    const isDiffCurrency = editPayForm.currency !== showDetail.currency;
    if (isDiffCurrency && !editPayForm.exchange_rate) return toast.error("Falta el tipo de cambio");
    try {
      const { data } = await api.patch(`/sales/${showDetail.id}/payments/${editingPayment.id}`, {
        amount: parseFloat(editPayForm.amount),
        currency: editPayForm.currency,
        bank_id: editPayForm.bank_id,
        exchange_rate: isDiffCurrency ? parseFloat(editPayForm.exchange_rate) : null,
        note: editPayForm.note,
        reason: editPayForm.reason,
      });
      toast.success("Pago actualizado");
      setShowDetail(data); setEditingPayment(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const openChangeSeller = (sale) => {
    setChangeSellerFor(sale);
    setChangeSellerForm({ seller_user_id: sale.seller_user_id || "", reason: "" });
  };

  const submitChangeSeller = async (e) => {
    e.preventDefault();
    if (!changeSellerFor || !changeSellerForm.seller_user_id) return toast.error("Selecciona un vendedor");
    if (changeSellerFor.status !== "draft" && !changeSellerForm.reason.trim()) {
      return toast.error("Captura una justificación para reasignar el vendedor");
    }
    setSavingSellerChange(true);
    try {
      const { data } = await api.patch(`/sales/${changeSellerFor.id}/seller`, {
        seller_user_id: changeSellerForm.seller_user_id,
        reason: changeSellerForm.reason.trim(),
      });
      toast.success(`Vendedor reasignado a ${data.seller_name || data.seller_email}`);
      setShowDetail(data);
      setChangeSellerFor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cambiar el vendedor");
    } finally { setSavingSellerChange(false); }
  };

  const submitDueEdit = async (e) => {
    e.preventDefault();
    if (!showDetail) return;
    try {
      const { data } = await api.patch(`/sales/${showDetail.id}/due-date`, {
        due_date: dueEditForm.due_date, reason: dueEditForm.reason,
      });
      toast.success("Fecha de vencimiento actualizada");
      setShowDetail(data); setShowDueEdit(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const openRepairQuote = (s) => {
    setRepairQuoteFor(s);
    setRepairQuoteAmount(s.total > 0 ? String(s.total) : "");
  };

  const submitRepairQuote = async (e) => {
    e?.preventDefault?.();
    if (!repairQuoteFor) return;
    const amt = Number(repairQuoteAmount);
    if (Number.isNaN(amt) || amt < 0) {
      toast.error("Monto inválido");
      return;
    }
    setSavingRepairQuote(true);
    try {
      await api.patch(`/sales/${repairQuoteFor.id}/repair-quote`, { amount: amt });
      toast.success(`Cotización actualizada · ${fmt(amt, repairQuoteFor.currency)}`);
      setRepairQuoteFor(null);
      setRepairQuoteAmount("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar la cotización");
    } finally {
      setSavingRepairQuote(false);
    }
  };

  // For non-super-admin users `/banks` returns only id+name (no currency / no balance).
  // In that case skip the currency filter so the dropdown still has options.
  const banksHaveCurrency = banks.some((b) => b.currency);
  const banksForPayment = banksHaveCurrency
    ? banks.filter((b) => b.currency === paymentForm.currency)
    : banks;

  const filtered = sales.filter((s) => {
    if (statusFilter === "repairs") {
      if (!s.is_repair_note) return false;
    } else if (statusFilter !== "all" && s.display_status !== statusFilter) {
      return false;
    }
    const payCount = s.payments?.length || 0;
    if (paymentFilter === "receivable" && Number(s.outstanding || 0) <= 0.005) return false;
    if (paymentFilter === "no_payments" && payCount > 0) return false;
    if (paymentFilter === "partial" && payCount === 0) return false;
    if (paymentFilter === "multi" && payCount < 2) return false;
    if (!search.trim()) return true;
    return tokenMatch(
      search,
      String(s.folio),
      String(s.folio).padStart(6, "0"),
      s.client_snapshot?.name,
      s.client_snapshot?.business_name,
      s.client_snapshot?.display_name,
      s.client_snapshot?.city,
      s.client_snapshot?.phone,
      s.seller_name,
      s.seller_email,
      s.notes,
      // Items: product names + characteristics joined
      (s.items || []).map((it) => `${it.product_name} ${it.characteristics || ""}`).join(" "),
    );
  });

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">VENTAS</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Notas de Venta</h1>
          <p className="text-zinc-500 mt-1 text-sm">{sales.length} notas · {filtered.length} visibles</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setRepairOpen(true)} data-testid="new-repair-btn" variant="outline" className="border-[#E0000F] text-[#E0000F] hover:bg-[#E0000F] hover:text-white gap-2">
            <FirstAidKit size={16} weight="bold" /> Nota de reparación
          </Button>
          <Button onClick={() => openNew(true)} data-testid="new-quote-btn" variant="outline" className="border-[#7E57C2] text-[#7E57C2] hover:bg-[#7E57C2] hover:text-white gap-2">
            <ClipboardText size={16} weight="bold" /> Nueva cotización
          </Button>
          <Button onClick={() => openNew(false)} data-testid="new-sale-btn" className="bg-[#002FA7] hover:bg-[#002277] text-white gap-2">
            <Plus size={16} weight="bold" /> Nueva nota
          </Button>
        </div>
      </header>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar folio, cliente, producto, vendedor…" className="pl-9" data-testid="sales-search" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {[
            {v:"all",label:"Todas",color:"#002FA7"},
            {v:"repairs",label:"Reparaciones",color:"#E0000F"},
            ...Object.entries(STATUS_META).map(([k,v])=>({v:k,label:v.label,color:v.color})),
          ].map((s) => (
            <button key={s.v} onClick={()=>setStatusFilter(s.v)}
              data-testid={`status-filter-${s.v}`}
              className={`px-3 py-1 text-xs font-medium rounded-sm border transition-all inline-flex items-center gap-1 ${
                statusFilter===s.v ? "border-transparent text-white" : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400"
              }`}
              style={statusFilter===s.v ? { background: s.color } : {}}
            >
              {s.v === "repairs" && <FirstAidKit size={11} weight="bold" />}
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs font-mono-data uppercase tracking-wider text-zinc-500">Pagos:</span>
        {PAYMENT_FILTERS.map((p) => (
          <button key={p.v} onClick={()=>setPaymentFilter(p.v)}
            data-testid={`payment-filter-${p.v}`}
            className={`px-3 py-1 text-xs font-medium rounded-sm border transition-all ${
              paymentFilter===p.v ? "border-transparent text-white" : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400"
            }`}
            style={paymentFilter===p.v ? { background: p.color } : {}}
          >{p.label}</button>
        ))}
      </div>

      {paymentFilter === "receivable" && filtered.length > 0 && (
        <ReceivableSummary sales={filtered} data-testid="receivable-summary"/>
      )}

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="sales-table">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Folio</th>
              <th className="text-left px-4 py-3">Estatus</th>
              <th className="text-left px-4 py-3">Cliente</th>
              <th className="text-left px-4 py-3">Vendedor</th>
              <th className="text-left px-4 py-3">F. Factura</th>
              <th className="text-left px-4 py-3">F. Vencimiento</th>
              <th className="text-center px-4 py-3">Abonos</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-right px-4 py-3">Pagado</th>
              <th className="text-right px-4 py-3">Saldo</th>
              {hasRole(user, "super_admin") && (
                <th className="text-center px-4 py-3">Ganancia</th>
              )}
              <th className="text-right px-4 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={hasRole(user, "super_admin") ? 12 : 11} className="px-4 py-16 text-center text-zinc-400 dotted-bg">Sin notas</td></tr>
            )}
            {filtered.map((s) => {
              const st = STATUS_META[s.display_status] || STATUS_META.sent;
              const Icon = st.Icon;
              const isRepair = !!s.is_repair_note;
              return (
                <tr key={s.id} className={`border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer ${isRepair ? "bg-[#FFF4E6]/60 hover:bg-[#FFE9CC]/70" : ""}`} onClick={()=>setShowDetail(s)} data-testid={`sale-row-${s.id}`}>
                  <td className="px-4 py-3 font-mono-data font-semibold">
                    <div className="flex items-center gap-1.5">
                      {isRepair && (
                        <span title="Nota de reparación" className="inline-flex items-center justify-center w-5 h-5 rounded bg-[#E0000F] text-white">
                          <Wrench size={11} weight="bold"/>
                        </span>
                      )}
                      #{String(s.folio).padStart(6,"0")}
                    </div>
                    {isRepair && (
                      <div className="text-[10px] uppercase tracking-widest text-[#E0000F] font-semibold mt-0.5">Reparación</div>
                    )}
                    {isRepair && s.repair_device && <AccessoryChips device={s.repair_device} size="xs" className="mt-1" testid={`sale-acc-${s.id}`} />}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-sm" style={{ background: st.color+"20", color: st.color }}>
                      <Icon size={11} weight="bold"/> {st.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{clientLabel(s)}</div>
                    {s.client_snapshot?.business_name && (s.client_display_as !== "business") && (s.client_display_as || s.client_snapshot?.name) !== s.client_snapshot?.business_name && (
                      <div className="text-xs text-zinc-500">{s.client_snapshot.business_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {s.seller_name ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-[#002FA7]/10 text-[#002FA7] font-medium">
                        {s.seller_name}
                      </span>
                    ) : (
                      <span className="text-zinc-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-600 text-xs">{s.invoice_date}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className={s.display_status === "overdue" ? "text-[#FFC000] font-semibold" : "text-zinc-600"}>{s.due_date}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {(s.payments?.length || 0) === 0 ? (
                      <span className="text-xs text-zinc-300">—</span>
                    ) : (
                      <span
                        className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-sm ${s.payments.length >= 2 ? "bg-[#7E57C2]/15 text-[#7E57C2]" : "bg-[#00873D]/15 text-[#00873D]"}`}
                        title={`${s.payments.length} ${s.payments.length === 1 ? "pago registrado" : "pagos registrados"}`}
                        data-testid={`payments-badge-${s.id}`}
                      >
                        <ListChecks size={11} weight="bold"/>
                        {s.payments.length} {s.payments.length === 1 ? "pago" : "pagos"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right numeric font-semibold">{fmt(s.total, s.currency)}</td>
                  <td className="px-4 py-3 text-right numeric text-[#00873D]">{fmt(s.paid_total, s.currency)}</td>
                  <td className="px-4 py-3 text-right numeric font-semibold" style={{ color: s.outstanding > 0 ? "#E0000F" : "#52525B" }}>
                    {fmt(s.outstanding, s.currency)}
                  </td>
                  {hasRole(user, "super_admin") && (
                    <td className="px-4 py-3 text-center" data-testid={`profit-cell-${s.id}`}>
                      <ProfitPill summary={s.profit_summary} />
                    </td>
                  )}
                  <td className="px-4 py-3" onClick={(e)=>e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      {isRepair && (s.status === "draft" || s.status === "quote") && (
                        <button
                          onClick={() => openRepairQuote(s)}
                          title={s.total > 0 ? "Actualizar cotización" : "Poner precio cotizado"}
                          className="p-2 hover:bg-orange-100 rounded text-[#E0000F]"
                          data-testid={`repair-quote-${s.id}`}
                        >
                          <CurrencyDollar size={15} weight="bold" />
                        </button>
                      )}
                      {s.is_quote && !s.converted_to_sale_id && (
                        <button
                          onClick={() => convertQuote(s)}
                          title="Convertir en nota de venta"
                          className="p-2 hover:bg-emerald-100 rounded text-[#00873D]"
                          data-testid={`convert-quote-${s.id}`}
                        >
                          <ArrowRight size={15} weight="bold" />
                        </button>
                      )}
                      <button onClick={()=>downloadPdf(s)} title="PDF" className="p-2 hover:bg-zinc-200 rounded text-[#E0000F]" data-testid={`pdf-sale-${s.id}`}>
                        <FilePdf size={15} weight="duotone" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create form */}
      <Dialog open={showForm} onOpenChange={(o)=>{ setShowForm(o); if (!o) setEditingSaleId(null); }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="sale-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              {editingSaleId ? (isQuoteMode ? "Editar cotización" : "Editar nota de venta") : (isQuoteMode ? "Nueva cotización" : "Nueva nota de venta")}
              {isQuoteMode && (
                <span className="text-[10px] font-mono-data font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#7E57C2] text-white">COTIZACIÓN</span>
              )}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {editingSaleId && editingSaleStatus !== "draft" && (
              <div className="border border-[#FFC000]/40 bg-[#FFC000]/10 p-4 rounded-sm">
                <Label className="text-xs uppercase tracking-wider text-[#996F00] flex items-center gap-2">
                  ⚠ Editando una nota fuera de Borrador — Justificación obligatoria
                </Label>
                <textarea
                  value={editReason}
                  onChange={(e)=>setEditReason(e.target.value)}
                  required
                  rows={2}
                  className="mt-2 w-full border border-zinc-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#996F00]"
                  placeholder="Ej: el cliente solicitó cambiar cantidad, error de captura, descuento autorizado por gerencia, etc."
                  data-testid="sale-edit-reason"
                />
                <p className="text-[10px] text-[#996F00] mt-1.5">Quedará registrada en el historial de auditoría de la nota.</p>
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs uppercase tracking-wider">Cliente *</Label>
                <Button type="button" size="sm" variant="outline" onClick={()=>setShowClientForm(true)} className="gap-1 h-7 text-xs border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5">
                  <UserPlus size={13} weight="bold" /> Nuevo cliente
                </Button>
              </div>
              <Popover open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={clientPickerOpen}
                    data-testid="sale-client"
                    className="mt-1 w-full flex items-center justify-between border border-zinc-300 rounded-md bg-white px-3 py-2 text-sm hover:border-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
                  >
                    <span className={clientId ? "" : "text-zinc-400"}>
                      {(() => {
                        const c = clients.find((x) => x.id === clientId);
                        if (!c) return "Selecciona un cliente…";
                        return `${c.display_name || c.name}${c.business_name ? ` · ${c.business_name}` : ""} (${c.currency})`;
                      })()}
                    </span>
                    <MagnifyingGlass size={14} className="text-zinc-400 ml-2 shrink-0"/>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                  <Command shouldFilter>
                    <CommandInput placeholder="Buscar por nombre, negocio, ciudad o teléfono…" data-testid="sale-client-search"/>
                    <CommandList>
                      <CommandEmpty>Sin resultados.</CommandEmpty>
                      <CommandGroup>
                        {clients.map((c) => {
                          const label = `${c.display_name || c.name}${c.business_name ? ` · ${c.business_name}` : ""}`;
                          const searchable = [
                            c.display_name, c.name, c.business_name, c.city, c.phone, c.email, c.currency,
                          ].filter(Boolean).join(" ").toLowerCase();
                          return (
                            <CommandItem
                              key={c.id}
                              value={searchable}
                              onSelect={() => { setClientId(c.id); setClientPickerOpen(false); }}
                              className="cursor-pointer"
                              data-testid={`sale-client-option-${c.id}`}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{label}</span>
                                <span className="text-[10px] text-zinc-500">
                                  {[c.city, c.phone, c.currency].filter(Boolean).join(" · ")}
                                </span>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {(() => {
                const c = clients.find((x) => x.id === clientId);
                if (!c || !c.business_name) return null;
                const currentAuto = c.display_name || c.name;
                return (
                  <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                    <span className="text-zinc-500 uppercase tracking-widest font-mono-data">Aparecer como:</span>
                    <button
                      type="button"
                      onClick={() => setClientDisplayAs("name")}
                      className={`px-2 py-0.5 border rounded-sm transition-colors ${(clientDisplayAs === "name") ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}
                      data-testid="display-as-name"
                    >
                      Nombre · {c.name}
                    </button>
                    <button
                      type="button"
                      onClick={() => setClientDisplayAs("business")}
                      className={`px-2 py-0.5 border rounded-sm transition-colors ${(clientDisplayAs === "business") ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}
                      data-testid="display-as-business"
                    >
                      Negocio · {c.business_name}
                    </button>
                    <button
                      type="button"
                      onClick={() => setClientDisplayAs("")}
                      className={`px-2 py-0.5 border rounded-sm transition-colors ${(!clientDisplayAs) ? "bg-zinc-100 border-zinc-300 text-zinc-700" : "bg-white border-zinc-200 text-zinc-500 hover:border-zinc-400"}`}
                      data-testid="display-as-auto"
                      title={`Automático (${currentAuto})`}
                    >
                      Auto
                    </button>
                  </div>
                );
              })()}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider">Vendedor que realizó la venta *</Label>
                <Select value={sellerId} onValueChange={setSellerId}>
                  <SelectTrigger className="mt-1" data-testid="sale-seller"><SelectValue placeholder="Selecciona el vendedor..."/></SelectTrigger>
                  <SelectContent>
                    {sellers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} {s.email === user?.email ? "· (tú)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-zinc-400 mt-1">Por defecto eres tú. Cámbialo si la venta la cerró otro vendedor.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <DatePicker label="Fecha de factura" value={invoiceDate} onChange={setInvoiceDate} allowPast />
                <DatePicker label="Fecha de vencimiento" value={dueDate} onChange={setDueDate} minToday />
              </div>
            </div>

            <div className="border border-zinc-200 rounded-sm overflow-hidden">
              <div className="bg-zinc-50 px-4 py-2 flex items-center justify-between gap-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Productos / Conceptos</div>
                <div className="flex gap-2">
                  <Button type="button" size="sm" onClick={addItem} variant="outline" className="gap-1 h-7" data-testid="sale-add-item">
                    <Plus size={13} weight="bold" /> Del inventario
                  </Button>
                  <Button type="button" size="sm" onClick={addCustomItem} variant="outline" className="gap-1 h-7 border-[#FFC000]/50 text-[#a87a00] hover:bg-[#FFC000]/10" data-testid="sale-add-custom">
                    <Plus size={13} weight="bold" /> Concepto libre
                  </Button>
                </div>
              </div>
              <div className="p-3 space-y-2">
                {items.length === 0 && <div className="text-xs text-zinc-400 text-center py-6">Agrega productos del inventario o conceptos libres (servicios, reparaciones, etc.)</div>}
                {items.map((it, i) => {
                  const prod = !it.is_custom ? products.find((p) => p.id === it.product_id) : null;
                  const isDragOver = dragOverIdx?.idx === i;
                  return (
                    <div
                      key={`item-${i}-${it.product_id || "custom"}`}
                      onDragOver={onItemDragOver(i)}
                      onDrop={onItemDrop(i)}
                      onDragEnd={onItemDragEnd}
                      onDragLeave={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) setDragOverIdx(null);
                      }}
                      className={`relative pl-7 grid grid-cols-12 gap-2 items-end p-2 rounded-sm transition-colors ${it.is_custom ? "bg-[#FFC000]/5 border border-[#FFC000]/20" : ""} ${draggingIdx === i ? "opacity-40" : ""}`}
                      data-testid={`sale-item-${i}`}
                    >
                      {/* Drop indicator lines */}
                      {isDragOver && dragOverIdx.pos === "above" && (
                        <div className="absolute left-2 right-2 -top-1 h-0.5 bg-[#002FA7] rounded-full pointer-events-none" data-testid={`drop-indicator-above-${i}`} />
                      )}
                      {isDragOver && dragOverIdx.pos === "below" && (
                        <div className="absolute left-2 right-2 -bottom-1 h-0.5 bg-[#002FA7] rounded-full pointer-events-none" data-testid={`drop-indicator-below-${i}`} />
                      )}
                      {/* Drag handle */}
                      <div
                        draggable={items.length > 1}
                        onDragStart={onItemDragStart(i)}
                        onDragEnd={onItemDragEnd}
                        className={`absolute left-0 top-0 bottom-0 w-6 flex items-center justify-center text-zinc-300 hover:text-zinc-700 hover:bg-zinc-100 rounded-l-sm transition-colors ${items.length > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-not-allowed opacity-40"}`}
                        title={items.length > 1 ? "Arrastra para reordenar" : "Necesitas al menos 2 conceptos para reordenar"}
                        data-testid={`drag-handle-${i}`}
                      >
                        <DotsSixVertical size={14} weight="bold" />
                      </div>
                      <div className="col-span-12 md:col-span-5">
                        <Label className="text-[10px] uppercase tracking-wider flex items-center gap-1">
                          {it.is_custom ? (<><span className="px-1.5 py-0.5 rounded-sm bg-[#FFC000]/20 text-[#a87a00] font-bold">LIBRE</span> Concepto</>) : "Producto del inventario"}
                        </Label>
                        {it.is_custom ? (
                          <div className="space-y-1 mt-1">
                            <Input
                              value={it.custom_name}
                              onChange={(e)=>updateItem(i,{custom_name:e.target.value})}
                              placeholder="Ej: Reparación de laptop, Servicio técnico..."
                              required
                              className="h-9"
                              data-testid={`sale-item-custom-name-${i}`}
                            />
                            <Input
                              value={it.custom_description}
                              onChange={(e)=>updateItem(i,{custom_description:e.target.value})}
                              placeholder="Descripción (opcional)"
                              className="h-8 text-xs"
                            />
                          </div>
                        ) : (
                          <>
                            <ProductPicker
                              products={products}
                              value={it.product_id}
                              onChange={(v)=>updateItem(i, { product_id: v })}
                              testId={`sale-item-product-${i}`}
                            />
                            {prod && <div className="text-[10px] text-zinc-500 mt-1 numeric">
                              Mín {fmt(prod.min_price, prod.sale_currency)} · Rev {fmt(prod.reseller_price, prod.sale_currency)} · Dist {fmt(prod.marketplace_price, prod.sale_currency)} · Tienda {fmt(prod.store_price, prod.sale_currency)}
                            </div>}
                            {it.product_id && (
                              <div className="mt-1.5">
                                <Textarea
                                  value={it.custom_description || ""}
                                  onChange={(e)=>updateItem(i,{custom_description:e.target.value})}
                                  rows={2}
                                  placeholder="Descripción que aparecerá en la nota (puedes editarla sin afectar el catálogo)"
                                  className="text-xs resize-none"
                                  data-testid={`sale-item-description-${i}`}
                                />
                                <div className="text-[9px] text-zinc-400 mt-0.5 italic">Solo afecta esta nota. El catálogo no se modifica.</div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <div className="col-span-3 md:col-span-2">
                        <Label className="text-[10px] uppercase tracking-wider">Cantidad</Label>
                        <Input type="number" min="1" value={it.qty} onChange={(e)=>updateItem(i,{qty:e.target.value})} className="mt-1 h-9 numeric"/>
                      </div>
                      {!it.is_custom ? (
                        <div className="col-span-4 md:col-span-2">
                          <Label className="text-[10px] uppercase tracking-wider">Tier</Label>
                          <Select value={it.tier} onValueChange={(v)=>updateItem(i,{tier:v})}>
                            <SelectTrigger className="mt-1 h-9"><SelectValue/></SelectTrigger>
                            <SelectContent>{TIERS.map((t)=><SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <div className="col-span-4 md:col-span-2">
                          <Label className="text-[10px] uppercase tracking-wider">Moneda</Label>
                          <Select value={it.currency} onValueChange={(v)=>updateItem(i,{currency:v})}>
                            <SelectTrigger className="mt-1 h-9"><SelectValue/></SelectTrigger>
                            <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="col-span-4 md:col-span-2">
                        <Label className="text-[10px] uppercase tracking-wider">Precio U.</Label>
                        <Input type="number" step="0.01" value={it.unit_price} onChange={(e)=>updateItem(i,{unit_price:e.target.value, tier: it.is_custom ? "custom" : "custom"})} className="mt-1 h-9 numeric"/>
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button type="button" onClick={()=>removeItem(i)} className="p-2 text-[#E0000F] hover:bg-red-100 rounded"><Trash size={14}/></button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {items.length > 0 && (
                <div className="bg-zinc-50 border-t border-zinc-200 divide-y divide-zinc-200">
                  <div className="px-4 py-2 flex items-center justify-between">
                    <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">Subtotal</div>
                    <div className="numeric text-sm font-semibold text-zinc-700" data-testid="sale-subtotal">
                      {fmt(subtotal, items[0]?.currency || saleCurrency)}
                    </div>
                  </div>
                  <div className="px-4 py-2 flex items-center justify-between gap-3">
                    <div className="text-xs uppercase font-mono-data tracking-wider text-[#E0000F] shrink-0">Descuento</div>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-[#E0000F] font-semibold">−</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={discount}
                        onChange={(e) => setDiscount(e.target.value)}
                        className="h-8 w-32 text-right numeric text-sm border-[#E0000F]/30"
                        placeholder="0.00"
                        data-testid="sale-discount"
                      />
                      <span className="text-xs text-zinc-500 ml-1">{items[0]?.currency || saleCurrency}</span>
                    </div>
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between bg-[#002FA7]/5">
                    <div className="text-sm font-semibold text-zinc-800">Total</div>
                    <div className="numeric text-xl font-bold text-[#002FA7]" data-testid="sale-total">{fmt(total, items[0]?.currency || saleCurrency)}</div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider">Notas / observaciones</Label>
              <Textarea value={notes} onChange={(e)=>setNotes(e.target.value)} rows={2} className="mt-1" placeholder="Notas internas (no aparecen en el PDF)" data-testid="sale-notes" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider">Términos y condiciones (PDF)</Label>
              <Textarea
                value={extraTerms}
                onChange={(e)=>setExtraTerms(e.target.value)}
                rows={3}
                className="mt-1"
                placeholder="Solo para esta nota. Si lo dejas vacío se usa el pie de página por defecto configurado en Ajustes."
                data-testid="sale-extra-terms"
              />
              <p className="text-[10px] text-zinc-400 mt-1">Este texto aparecerá en el PDF de la nota. Sobreescribe el pie de página por defecto solo para esta venta.</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setShowForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className={`${isQuoteMode && !editingSaleId ? "bg-[#7E57C2] hover:bg-[#6b46b2]" : "bg-[#002FA7] hover:bg-[#002277]"} text-white`} data-testid="sale-submit">
                {saving ? "Guardando…" : (editingSaleId ? "Actualizar" : (isQuoteMode ? "Crear cotización" : "Crear nota (borrador)"))}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick create client */}
      <Dialog open={showClientForm} onOpenChange={setShowClientForm}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Nuevo cliente</DialogTitle></DialogHeader>
          <form onSubmit={submitNewClient} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider">Nombre *</Label>
                <Input value={newClient.name} onChange={(e)=>setNewClient({...newClient,name:e.target.value})} required className="mt-1"/>
              </div>
              <div><Label className="text-xs uppercase tracking-wider">Negocio</Label>
                <Input value={newClient.business_name} onChange={(e)=>setNewClient({...newClient,business_name:e.target.value})} className="mt-1"/></div>
              <div><Label className="text-xs uppercase tracking-wider">Aparecer como</Label>
                <Input value={newClient.display_name} onChange={(e)=>setNewClient({...newClient,display_name:e.target.value})} placeholder="Auto" className="mt-1"/></div>
              <div><Label className="text-xs uppercase tracking-wider">Ciudad</Label>
                <Input value={newClient.city} onChange={(e)=>setNewClient({...newClient,city:e.target.value})} className="mt-1"/></div>
              <div><Label className="text-xs uppercase tracking-wider">Teléfono</Label>
                <Input value={newClient.phone} onChange={(e)=>setNewClient({...newClient,phone:e.target.value})} className="mt-1"/></div>
              <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider">Dirección</Label>
                <Input value={newClient.address} onChange={(e)=>setNewClient({...newClient,address:e.target.value})} className="mt-1"/></div>
              <div><Label className="text-xs uppercase tracking-wider">Moneda</Label>
                <Select value={newClient.currency} onValueChange={(v)=>setNewClient({...newClient,currency:v})}>
                  <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                </Select></div>
              <div><Label className="text-xs uppercase tracking-wider">Tipo</Label>
                <Select value={newClient.client_type} onValueChange={(v)=>setNewClient({...newClient,client_type:v})}>
                  <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="final">Final</SelectItem><SelectItem value="reseller">Revendedor</SelectItem><SelectItem value="wholesale">Mayorista</SelectItem><SelectItem value="distributor">Distribuidor</SelectItem></SelectContent>
                </Select></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setShowClientForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={savingClient} className="bg-[#002FA7] hover:bg-[#002277] text-white">{savingClient ? "Creando…" : "Crear y seleccionar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!showDetail} onOpenChange={(o)=>{ if (!o) setShowDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="sale-detail-dialog">
          {showDetail && (() => {
            const st = STATUS_META[showDetail.display_status] || STATUS_META.sent;
            const Icon = st.Icon;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl flex items-center gap-3">
                    Nota #{String(showDetail.folio).padStart(6,"0")}
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-sm" style={{ background: st.color+"20", color: st.color }}>
                      <Icon size={11} weight="bold"/> {st.label}
                    </span>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {showDetail.status === "canceled" && (
                    <div className="p-3 border-l-4 border-red-500 bg-red-50 rounded-r" data-testid="canceled-banner">
                      <div className="text-xs font-bold uppercase tracking-wider text-red-700">Nota cancelada</div>
                      <div className="text-xs text-red-800 mt-0.5">
                        Cancelada por <b>{showDetail.canceled_by_name || showDetail.canceled_by_email || "—"}</b>
                        {showDetail.canceled_at && <> el {new Date(showDetail.canceled_at).toLocaleString("es-MX")}</>}
                        {showDetail.previous_status && <> · Estado previo: <b>{STATUS_META[showDetail.previous_status]?.label || showDetail.previous_status}</b></>}
                      </div>
                      <div className="text-xs text-red-700 mt-1">Stock devuelto al inventario. Usa &ldquo;Restaurar para venta&rdquo; para reactivarla.</div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-zinc-500">Cliente</div>
                      <div className="font-medium">{clientLabel(showDetail)}</div>
                      <div className="text-xs text-zinc-500">{showDetail.client_snapshot?.business_name && showDetail.client_display_as !== "business" ? showDetail.client_snapshot.business_name : ""}</div>
                      <div className="text-xs text-zinc-500">{showDetail.client_snapshot?.city}</div>
                    </div>
                    <div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <div className="uppercase tracking-wider text-zinc-500">F. Factura</div>
                          <div className="font-medium">{showDetail.invoice_date}</div>
                        </div>
                        <div>
                          <div className="uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                            F. Vencimiento
                            {showDetail.status !== "paid" && (
                              <button onClick={()=>{ setDueEditForm({due_date: showDetail.due_date, reason: ""}); setShowDueEdit(true); }} className="text-[#002FA7] hover:underline">editar</button>
                            )}
                          </div>
                          <div className={`font-medium ${showDetail.display_status==="overdue"?"text-[#FFC000]":""}`}>{showDetail.due_date}</div>
                        </div>
                      </div>
                      <div className="text-xs uppercase tracking-wider text-zinc-500 mt-2 flex items-center gap-1.5">
                        Vendedor
                        {(hasRole(user, "super_admin") || hasRole(user, "sales")) && (
                          <button
                            onClick={()=>openChangeSeller(showDetail)}
                            data-testid={`change-seller-btn-${showDetail.id}`}
                            className="text-[#002FA7] hover:underline normal-case tracking-normal"
                          >
                            cambiar
                          </button>
                        )}
                      </div>
                      <div className="text-xs font-medium">{showDetail.seller_name || showDetail.seller_email || showDetail.created_by_email}</div>
                    </div>
                  </div>

                  {showDetail.is_repair_note && showDetail.repair_device && (
                    <div className="border border-[#E0000F]/20 bg-[#FFF4E6]/50 p-3 text-xs" data-testid="sale-repair-device">
                      <div className="text-[10px] uppercase tracking-widest text-[#E0000F] font-semibold">Equipo del cliente · qué nos entregó</div>
                      <div className="flex flex-wrap items-start gap-4 mt-1.5">
                        <div>
                          <div className="font-medium text-zinc-900">{showDetail.repair_device.type_label} · {showDetail.repair_device.brand} {showDetail.repair_device.model}</div>
                          {showDetail.repair_device.hw && <div className="text-zinc-600">{showDetail.repair_device.hw}</div>}
                          {showDetail.repair_device.extras && <div className="text-zinc-500 text-[11px]">{showDetail.repair_device.extras}</div>}
                        </div>
                        <AccessoryChips device={showDetail.repair_device} testid="sale-repair-acc" />
                      </div>
                    </div>
                  )}

                  <table className="w-full text-sm border border-zinc-200">
                    <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                      <tr>
                        <th className="text-left px-3 py-2">Producto</th>
                        <th className="text-right px-3 py-2">Cant.</th>
                        <th className="text-right px-3 py-2">P.U.</th>
                        <th className="text-right px-3 py-2">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {showDetail.items.map((it, i) => (
                        <tr key={`${it.product_id || "custom"}-${i}`} className="border-t border-zinc-100">
                          <td className="px-3 py-2"><div className="font-medium">{it.product_name}</div><div className="text-[11px] text-zinc-500 whitespace-pre-line">{it.characteristics}</div></td>
                          <td className="px-3 py-2 text-right numeric">{it.qty}</td>
                          <td className="px-3 py-2 text-right numeric">{fmt(it.unit_price, it.currency)}</td>
                          <td className="px-3 py-2 text-right numeric">{fmt(it.subtotal, it.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {showDetail.discount > 0 && (
                    <div className="border border-[#E0000F]/30 bg-[#E0000F]/5 rounded-sm p-3 flex items-center justify-between" data-testid="sale-detail-discount">
                      <div className="text-xs uppercase tracking-wider text-[#E0000F] inline-flex items-center gap-1.5">
                        Descuento aplicado
                      </div>
                      <div className="text-right">
                        <div className="numeric text-sm text-zinc-500">Subtotal: {fmt(showDetail.subtotal ?? (showDetail.total + showDetail.discount), showDetail.currency)}</div>
                        <div className="numeric text-lg font-bold text-[#E0000F]">−{fmt(showDetail.discount, showDetail.currency)}</div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-3">
                    <div className="border border-zinc-200 p-3">
                      <div className="text-xs uppercase tracking-wider text-zinc-500">Total</div>
                      <div className="numeric font-bold text-lg">{fmt(showDetail.total, showDetail.currency)}</div>
                    </div>
                    <div className="border border-[#00873D]/30 bg-[#00873D]/5 p-3">
                      <div className="text-xs uppercase tracking-wider text-[#00873D]">Pagado</div>
                      <div className="numeric font-bold text-lg text-[#00873D]">{fmt(showDetail.paid_total, showDetail.currency)}</div>
                    </div>
                    <div className="border border-[#E0000F]/30 bg-[#E0000F]/5 p-3">
                      <div className="text-xs uppercase tracking-wider text-[#E0000F]">Saldo</div>
                      <div className="numeric font-bold text-lg text-[#E0000F]">{fmt(showDetail.outstanding, showDetail.currency)}</div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {showDetail.status === "draft" && (
                      <Button onClick={()=>markSent(showDetail)} className="bg-[#1E88E5] hover:bg-[#1565C0] text-white gap-1" data-testid="mark-sent-btn">
                        <PaperPlaneTilt size={14}/> Marcar Enviada
                      </Button>
                    )}
                    {(showDetail.status === "paid" || showDetail.status === "sent") && !showDetail.is_repair_note && (
                      <Button
                        onClick={()=>setSwapFor(showDetail)}
                        variant="outline"
                        className="border-[#7E57C2] text-[#7E57C2] hover:bg-[#7E57C2]/10 gap-1"
                        data-testid="open-swap-btn"
                      >
                        <ArrowsClockwise size={14}/> Cambio / Garantía
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={()=>openEditSale(showDetail)}
                      className="border-[#FFC000] text-[#996F00] hover:bg-[#FFC000]/10 gap-1"
                      title={(showDetail.payments?.length || 0) > 0 ? "Editar nota con pagos registrados — requiere justificación" : ""}
                      data-testid="edit-sale-btn"
                    >
                      <PencilSimple size={14}/> Editar
                    </Button>
                    {showDetail.status === "draft" && (
                      <Button variant="outline" onClick={()=>setToDelete(showDetail)} className="text-[#E0000F] border-[#E0000F]/30 gap-1" data-testid="delete-draft-btn">
                        <Trash size={14}/> Eliminar
                      </Button>
                    )}
                    {showDetail.status === "canceled" && (
                      <Button onClick={()=>restoreSale(showDetail)} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="restore-sale-btn">
                        <ArrowsClockwise size={14}/> Restaurar para venta
                      </Button>
                    )}
                    {showDetail.status !== "canceled" && showDetail.status !== "draft" && showDetail.outstanding > 0.005 && (
                      <Button onClick={()=>openPaymentDialog(showDetail)} className="bg-[#00873D] hover:bg-[#006e30] text-white gap-1" data-testid="add-payment-btn">
                        <CheckCircle size={14}/> Registrar pago
                      </Button>
                    )}
                    {showDetail.status === "draft" && showDetail.is_repair_note && showDetail.outstanding > 0.005 && (
                      <Button onClick={()=>openPaymentDialog(showDetail)} className="bg-[#00873D] hover:bg-[#006e30] text-white gap-1" data-testid="add-payment-btn">
                        <CheckCircle size={14}/> Registrar pago
                      </Button>
                    )}
                    {showDetail.status !== "draft" && showDetail.status !== "canceled" && (
                      <Button
                        variant="outline"
                        onClick={()=>setToDelete(showDetail)}
                        disabled={(showDetail.payments?.length || 0) > 0}
                        className="text-[#E0000F] border-[#E0000F]/30 gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={(showDetail.payments?.length || 0) > 0
                          ? "Primero reversa los pagos para poder cancelar la nota"
                          : "Cancela la nota y restaura el stock"}
                        data-testid="cancel-sale-btn"
                      >
                        <Trash size={14}/> Cancelar nota
                      </Button>
                    )}
                    <Button onClick={()=>downloadPdf(showDetail)} variant="outline" className="text-[#E0000F] border-[#E0000F]/30 gap-1">
                      <FilePdf size={14} weight="duotone" /> PDF
                    </Button>
                  </div>
                  {showDetail.status !== "draft" && (showDetail.payments?.length || 0) > 0 && (
                    <div className="text-[11px] text-zinc-500 -mt-1 px-0.5">
                      ⓘ Para cancelar esta nota primero debes <b>reversar todos los pagos</b> registrados (botón de basura en la fila del pago). Eso devuelve el dinero al banco correspondiente.
                    </div>
                  )}

                  {showDetail.payments?.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1"><ListChecks size={14}/> PAGOS</div>
                      <table className="w-full text-sm border border-zinc-200">
                        <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                          <tr>
                            <th className="text-left px-3 py-2">Fecha</th>
                            <th className="text-left px-3 py-2">Banco</th>
                            <th className="text-right px-3 py-2">Monto</th>
                            <th className="text-right px-3 py-2">Equiv.</th>
                            <th className="text-left px-3 py-2">Nota</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {showDetail.payments.map((p) => (
                            <tr key={p.id} className="border-t border-zinc-100">
                              <td className="px-3 py-2 text-xs text-zinc-600">{new Date(p.created_at).toLocaleString("es-MX")}</td>
                              <td className="px-3 py-2 font-medium">{p.bank_name}</td>
                              <td className="px-3 py-2 text-right numeric">{fmt(p.amount, p.currency)}</td>
                              <td className="px-3 py-2 text-right numeric text-zinc-500">{fmt(p.amount_in_sale_currency, p.sale_currency)}</td>
                              <td className="px-3 py-2 text-xs text-zinc-500">{p.note || "—"}</td>
                              <td className="px-3 py-2">
                                <div className="flex justify-end gap-1">
                                  <button onClick={()=>openEditPayment(p)} className="text-[#002FA7] hover:bg-blue-50 p-1.5 rounded" title="Editar pago" data-testid={`edit-pay-${p.id}`}>
                                    <PencilSimple size={12}/>
                                  </button>
                                  <button onClick={()=>{
                                    const reason = window.prompt("Motivo de la reversión:");
                                    if (reason !== null) removePayment(p.id, reason);
                                  }} className="text-[#E0000F] hover:bg-red-50 p-1.5 rounded" title="Revertir pago"><Trash size={12}/></button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {showDetail.swap_history?.length > 0 && (
                    <div className="border border-[#7E57C2]/30 bg-[#7E57C2]/5 rounded p-4" data-testid="swap-history">
                      <div className="flex items-center gap-2 mb-3">
                        <ArrowsClockwise size={16} weight="bold" className="text-[#7E57C2]" />
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-[#7E57C2]">
                          Historial de cambios / garantías ({showDetail.swap_history.length})
                        </h3>
                      </div>
                      <ul className="space-y-3">
                        {showDetail.swap_history.slice().reverse().map((s, i) => {
                          const cur = (n) => `${(s.currency || showDetail.currency) === "USD" ? "$" : "MX$"}${Number(n || 0).toLocaleString("en-US",{minimumFractionDigits:2})}`;
                          return (
                            <li key={i} className="bg-white border border-zinc-200 rounded p-3 text-sm" data-testid={`swap-entry-${i}`}>
                              <div className="flex items-center justify-between text-xs text-zinc-500 mb-2">
                                <span className="font-mono-data">{new Date(s.timestamp).toLocaleString("es-MX")}</span>
                                <span>por {s.user_email}</span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Equipo devuelto</div>
                                  {s.returned ? (
                                    <div className="font-medium text-zinc-800">
                                      {s.returned.product_name || "—"}
                                      {s.returned.qty > 1 && <span className="text-zinc-500"> · ×{s.returned.qty}</span>}
                                      {s.returned.missing && <span className="ml-1 text-xs text-amber-700">(no estaba en inventario)</span>}
                                    </div>
                                  ) : <div className="text-zinc-400 italic">Sin retorno</div>}
                                </div>
                                <div>
                                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Equipo nuevo</div>
                                  {s.new ? (
                                    <div className="font-medium text-zinc-800">
                                      {s.new.product_name}
                                      {s.new.free_text && <span className="ml-1 text-[10px] text-amber-700">(libre)</span>}
                                      {s.new.unit_price > 0 && <span className="text-zinc-500"> · {cur(s.new.unit_price)}</span>}
                                    </div>
                                  ) : <div className="text-zinc-400 italic">—</div>}
                                </div>
                              </div>
                              <div className="flex items-center justify-between mt-3 pt-2 border-t border-zinc-100">
                                <span className="text-xs text-zinc-700 italic">"{s.reason}"</span>
                                {s.diferencia > 0
                                  ? <span className="text-sm font-semibold text-[#00873D] numeric">+{cur(s.diferencia)} pagado</span>
                                  : <span className="text-xs text-zinc-400">Sin diferencia</span>}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {showDetail.audit_log?.length > 0 && (
                    <details>
                      <summary className="cursor-pointer text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1"><ClockCounterClockwise size={14}/> Historial de cambios ({showDetail.audit_log.length})</summary>
                      <div className="mt-2 space-y-1 text-xs">
                        {showDetail.audit_log.slice().reverse().map((a, i) => (
                          <div key={`${a.at || i}-${a.field || ""}-${i}`} className="flex gap-3 border-l-2 border-zinc-200 pl-3 py-1">
                            <span className="text-zinc-400 font-mono-data">{new Date(a.at).toLocaleString("es-MX")}</span>
                            <span className="font-medium">{a.field}</span>
                            <span className="text-zinc-500">{a.old !== null ? `${a.old} →` : "+"} <b>{a.new}</b></span>
                            <span className="text-zinc-400">por {a.user_email}</span>
                            {a.reason && <span className="text-zinc-600 italic">&ldquo;{a.reason}&rdquo;</span>}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}

                  {showDetail.notes && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-zinc-500">Notas</div>
                      <p className="text-sm">{showDetail.notes}</p>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent className="max-w-md" data-testid="payment-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Registrar pago</DialogTitle></DialogHeader>
          {showDetail && <form onSubmit={submitPayment} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Moneda del pago</Label>
                <Select value={paymentForm.currency} onValueChange={(v)=>setPaymentForm({...paymentForm,currency:v,bank_id:"",exchange_rate:""})}>
                  <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MXN">MXN</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Monto</Label>
                <Input type="number" step="0.01" min="0.01" value={paymentForm.amount} onChange={(e)=>setPaymentForm({...paymentForm,amount:e.target.value})} required className="mt-1 numeric" data-testid="pay-amount"/>
              </div>
            </div>
            {paymentForm.currency !== showDetail.currency && (
              <div className="border border-[#FFC000]/40 bg-[#FFC000]/10 p-3 rounded-sm">
                <Label className="text-xs uppercase tracking-wider">Tipo de cambio (1 USD = ? MXN)</Label>
                <Input type="number" step="0.01" min="0.01" value={paymentForm.exchange_rate} onChange={(e)=>setPaymentForm({...paymentForm,exchange_rate:e.target.value})} required className="mt-1 numeric" placeholder="Ej. 17.50" data-testid="pay-xr"/>
                <div className="text-xs text-zinc-600 mt-1">La nota es en {showDetail.currency}. Solo bancos en {paymentForm.currency} aparecerán abajo.</div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider">Banco que recibe</Label>
              <Select value={paymentForm.bank_id} onValueChange={(v)=>setPaymentForm({...paymentForm,bank_id:v})}>
                <SelectTrigger className="mt-1" data-testid="pay-bank"><SelectValue placeholder={banksForPayment.length ? "Selecciona..." : `Sin bancos ${paymentForm.currency}`}/></SelectTrigger>
                <SelectContent>
                  {banksForPayment.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}{b.currency ? ` (${b.currency})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Nota / referencia</Label>
              <Input value={paymentForm.note} onChange={(e)=>setPaymentForm({...paymentForm,note:e.target.value})} placeholder="abono parcial, depósito SPEI..." className="mt-1"/>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setShowPayment(false)} disabled={payingPayment}>Cancelar</Button>
              <Button type="submit" disabled={payingPayment} className="bg-[#00873D] hover:bg-[#006e30] text-white disabled:opacity-60 disabled:cursor-not-allowed" data-testid="pay-submit">
                {payingPayment ? "Registrando…" : "Registrar pago"}
              </Button>
            </DialogFooter>
          </form>}
        </DialogContent>
      </Dialog>

      {/* Edit payment */}
      <Dialog open={!!editingPayment} onOpenChange={(o)=>{ if (!o) setEditingPayment(null); }}>
        <DialogContent className="max-w-md" data-testid="edit-payment-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Editar pago</DialogTitle></DialogHeader>
          {showDetail && editingPayment && (
            <form onSubmit={submitEditPayment} className="space-y-3">
              <div className="bg-zinc-50 p-3 rounded-sm text-xs text-zinc-600 border border-zinc-200">
                <div className="font-semibold mb-1">Pago original:</div>
                <div><span className="font-mono-data">{editingPayment.amount} {editingPayment.currency}</span> en <b>{editingPayment.bank_name}</b></div>
                <div className="text-[10px] text-zinc-400">{new Date(editingPayment.created_at).toLocaleString("es-MX")}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Moneda del pago</Label>
                  <Select value={editPayForm.currency} onValueChange={(v)=>setEditPayForm({...editPayForm,currency:v,bank_id:"",exchange_rate:""})}>
                    <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MXN">MXN</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Monto</Label>
                  <Input type="number" step="0.01" min="0.01" value={editPayForm.amount} onChange={(e)=>setEditPayForm({...editPayForm,amount:e.target.value})} required className="mt-1 numeric"/>
                </div>
              </div>
              {editPayForm.currency !== showDetail.currency && (
                <div className="border border-[#FFC000]/40 bg-[#FFC000]/10 p-3 rounded-sm">
                  <Label className="text-xs uppercase tracking-wider">Tipo de cambio (1 USD = ? MXN)</Label>
                  <Input type="number" step="0.01" min="0.01" value={editPayForm.exchange_rate} onChange={(e)=>setEditPayForm({...editPayForm,exchange_rate:e.target.value})} required className="mt-1 numeric"/>
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-wider">Banco</Label>
                <Select value={editPayForm.bank_id} onValueChange={(v)=>setEditPayForm({...editPayForm,bank_id:v})}>
                  <SelectTrigger className="mt-1" data-testid="edit-pay-bank"><SelectValue placeholder="Banco..."/></SelectTrigger>
                  <SelectContent>
                    {(banksHaveCurrency ? banks.filter((b) => b.currency === editPayForm.currency) : banks).map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}{b.currency ? ` (${b.currency})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Nota</Label>
                <Input value={editPayForm.note} onChange={(e)=>setEditPayForm({...editPayForm,note:e.target.value})} className="mt-1"/>
              </div>
              <div className="border border-[#E0000F]/30 bg-[#E0000F]/5 p-3 rounded-sm">
                <Label className="text-xs uppercase tracking-wider text-[#E0000F]">Motivo del cambio *</Label>
                <Input value={editPayForm.reason} onChange={(e)=>setEditPayForm({...editPayForm,reason:e.target.value})} placeholder="Banco incorrecto, monto erróneo..." required className="mt-1" data-testid="edit-pay-reason"/>
                <p className="text-[10px] text-zinc-500 mt-1">Queda registrado en el historial de cambios.</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={()=>setEditingPayment(null)}>Cancelar</Button>
                <Button type="submit" className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="edit-pay-submit">Guardar cambios</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit due date */}
      <Dialog open={showDueEdit} onOpenChange={setShowDueEdit}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-xl">Editar fecha de vencimiento</DialogTitle></DialogHeader>
          <form onSubmit={submitDueEdit} className="space-y-3">
            <DatePicker label="Nueva fecha" value={dueEditForm.due_date} onChange={(v)=>setDueEditForm({...dueEditForm,due_date:v})} minToday />
            <div>
              <Label className="text-xs uppercase tracking-wider">Motivo del cambio</Label>
              <Input value={dueEditForm.reason} onChange={(e)=>setDueEditForm({...dueEditForm,reason:e.target.value})} placeholder="Cliente pidió extensión..." className="mt-1" required/>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setShowDueEdit(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#002FA7] hover:bg-[#002277] text-white">Actualizar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change seller (allowed even when sale is paid) */}
      <Dialog open={!!changeSellerFor} onOpenChange={(o)=>{ if (!o) setChangeSellerFor(null); }}>
        <DialogContent className="max-w-sm" data-testid="change-seller-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Reasignar vendedor</DialogTitle>
          </DialogHeader>
          {changeSellerFor && (
            <form onSubmit={submitChangeSeller} className="space-y-3">
              <div className="text-xs text-zinc-500">
                Nota <span className="font-semibold text-zinc-700">#{String(changeSellerFor.folio).padStart(6,"0")}</span>
                {" "}· vendedor actual: <span className="font-semibold text-zinc-700">{changeSellerFor.seller_name || changeSellerFor.seller_email || "—"}</span>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Nuevo vendedor</Label>
                <select
                  value={changeSellerForm.seller_user_id}
                  onChange={(e)=>setChangeSellerForm({...changeSellerForm, seller_user_id: e.target.value})}
                  data-testid="change-seller-select"
                  className="w-full mt-1 border border-zinc-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                  required
                >
                  <option value="">Selecciona…</option>
                  {sellers.map((s) => (
                    <option key={s.id} value={s.id}>{s.name || s.email}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">
                  Motivo {changeSellerFor.status !== "draft" && <span className="text-rose-500">*</span>}
                </Label>
                <Input
                  value={changeSellerForm.reason}
                  onChange={(e)=>setChangeSellerForm({...changeSellerForm, reason: e.target.value})}
                  placeholder="Corrección de asignación / cambio de equipo…"
                  className="mt-1"
                  data-testid="change-seller-reason"
                  required={changeSellerFor.status !== "draft"}
                />
              </div>
              {changeSellerFor.status !== "draft" && (
                <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  ⚠️ Esta nota ya tiene pagos. El cambio se registrará en el historial de auditoría con tu usuario y el motivo.
                </p>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={()=>setChangeSellerFor(null)}>Cancelar</Button>
                <Button type="submit" disabled={savingSellerChange} className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="change-seller-submit">
                  {savingSellerChange ? "Guardando…" : "Reasignar"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <RepairIntakeDialog open={repairOpen} onOpenChange={setRepairOpen} clients={clients} onClientsChanged={setClients} onCreated={load} />

      {/* Quick repair quote dialog */}
      <Dialog open={!!repairQuoteFor} onOpenChange={(o) => { if (!o) { setRepairQuoteFor(null); setRepairQuoteAmount(""); } }}>
        <DialogContent className="max-w-md" data-testid="repair-quote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench size={18} weight="fill" className="text-[#E0000F]" />
              Cotizar reparación
            </DialogTitle>
          </DialogHeader>
          {repairQuoteFor && (
            <form onSubmit={submitRepairQuote} className="space-y-4">
              <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 text-xs">
                <div className="font-mono-data font-semibold text-zinc-700">#{String(repairQuoteFor.folio).padStart(6,"0")}</div>
                <div className="text-zinc-500 mt-1">{repairQuoteFor.client_snapshot?.name || repairQuoteFor.client_snapshot?.business_name}</div>
                <div className="text-zinc-600 mt-1">{repairQuoteFor.items?.[0]?.product_name}</div>
                {repairQuoteFor.items?.[0]?.characteristics && (
                  <div className="text-zinc-400 italic mt-1 line-clamp-4 whitespace-pre-line">{repairQuoteFor.items[0].characteristics}</div>
                )}
              </div>
              <div>
                <Label className="text-xs">Precio cotizado al cliente</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-500 font-mono-data">{repairQuoteFor.currency === "USD" ? "US$" : "MX$"}</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={repairQuoteAmount}
                    onChange={(e) => setRepairQuoteAmount(e.target.value)}
                    autoFocus
                    className="pl-12 text-lg numeric font-semibold"
                    data-testid="repair-quote-amount"
                  />
                </div>
                <div className="text-[10px] text-zinc-400 mt-1">La nota se mantiene en Borrador hasta que la conviertas manualmente.</div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setRepairQuoteFor(null)}>Cancelar</Button>
                <Button type="submit" disabled={savingRepairQuote} className="bg-[#E0000F] hover:bg-[#c30414] text-white gap-1.5" data-testid="repair-quote-save">
                  <Check size={14} weight="bold" />
                  {savingRepairQuote ? "Guardando…" : "Guardar cotización"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o)=>{ if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="cancel-sale-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {toDelete?.status === "draft" ? "¿Eliminar nota borrador?" : "¿Cancelar nota de venta?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.status === "draft"
                ? <>Se eliminará la nota borrador <b>#{String(toDelete?.folio || 0).padStart(6,"0")}</b> y se restaurará el stock de los productos del catálogo.</>
                : <>
                    Se cancelará la nota <b>#{String(toDelete?.folio || 0).padStart(6,"0")}</b> (cliente: <b>{toDelete?.client_name || toDelete?.client?.name}</b>) y se restaurará el stock al inventario.
                    {toDelete?.status !== "draft" && (
                      <span className="block mt-2 text-emerald-700 text-xs"> La nota NO se elimina — quedará en estado <b>Cancelada</b> y puedes restaurarla después usando el botón &ldquo;Restaurar para venta&rdquo;.</span>
                    )}
                    <br /><br />
                    <span className="text-[11px] text-zinc-500">Conceptos libres no afectan el stock. Esta acción es <b>irreversible</b>.</span>
                  </>
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-sale-cancel">Volver</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#E0000F] hover:bg-[#b8000c] text-white" data-testid="cancel-sale-confirm">
              {toDelete?.status === "draft" ? "Sí, eliminar" : "Sí, cancelar nota"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <SwapDialog
        sale={swapFor}
        onClose={()=>setSwapFor(null)}
        onSwapped={(updated) => {
          setSwapFor(null);
          // Refresh detail + list
          setShowDetail(updated);
          load();
        }}
      />
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────
// ProfitPill — pill estético que muestra ganancia % de una nota.
// Solo visible para super_admin (la columna se renderiza condicionalmente).
// ───────────────────────────────────────────────────────────────────
const TIER_COLOR = {
  "Precio mínimo": { bg: "#E0000F", text: "#FFFFFF", soft: "rgba(224,0,15,.10)", solid: "#E0000F" },
  "Reventa":       { bg: "#7E57C2", text: "#FFFFFF", soft: "rgba(126,87,194,.10)", solid: "#7E57C2" },
  "Distribuidor":  { bg: "#FFC000", text: "#996F00", soft: "rgba(255,192,0,.15)", solid: "#996F00" },
  "Tienda":        { bg: "#002FA7", text: "#FFFFFF", soft: "rgba(0,47,167,.10)", solid: "#002FA7" },
  "Personalizado": { bg: "#52525B", text: "#FFFFFF", soft: "rgba(82,82,91,.10)", solid: "#52525B" },
  "Mixto":         { bg: "#1E88E5", text: "#FFFFFF", soft: "rgba(30,136,229,.10)", solid: "#1E88E5" },
};

function ProfitPill({ summary }) {
  if (!summary) return <span className="text-zinc-300 text-xs">—</span>;
  const { profit_pct, profit_amount, total_cost, pricing_label, has_partial, currency } = summary;
  const tone = TIER_COLOR[pricing_label] || TIER_COLOR["Personalizado"];

  // Color de % según rango
  let pctColor = "#52525B";
  let pctBg = "rgba(82,82,91,.10)";
  let Icon = TrendUp;
  if (profit_pct === null || profit_pct === undefined) {
    pctColor = "#52525B"; pctBg = "rgba(82,82,91,.10)";
  } else if (profit_pct >= 40) {
    pctColor = "#00873D"; pctBg = "rgba(0,135,61,.12)"; Icon = TrendUp;
  } else if (profit_pct >= 15) {
    pctColor = "#1E88E5"; pctBg = "rgba(30,136,229,.12)"; Icon = TrendUp;
  } else if (profit_pct >= 0) {
    pctColor = "#996F00"; pctBg = "rgba(255,192,0,.18)"; Icon = TrendUp;
  } else {
    pctColor = "#E0000F"; pctBg = "rgba(224,0,15,.10)"; Icon = TrendDown;
  }

  const pctLabel = profit_pct === null || profit_pct === undefined
    ? "s/c"
    : `${profit_pct > 0 ? "+" : ""}${profit_pct.toFixed(1)}%`;
  const profitMx = new Intl.NumberFormat("es-MX", { style: "currency", currency, maximumFractionDigits: 0 }).format(Math.abs(profit_amount));
  const costMx = new Intl.NumberFormat("es-MX", { style: "currency", currency, maximumFractionDigits: 0 }).format(total_cost);
  const tooltip = has_partial
    ? `Costo: ${costMx} · Ganancia: ${profit_amount < 0 ? "-" : ""}${profitMx}\n⚠ Cálculo parcial (productos sin costo registrado)`
    : `Costo: ${costMx} · Ganancia: ${profit_amount < 0 ? "-" : ""}${profitMx}`;

  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-md border overflow-hidden text-[11px] font-semibold align-middle shadow-sm"
      style={{ borderColor: pctColor + "33" }}
      title={tooltip}
      data-testid="profit-pill"
    >
      <span
        className="px-2 py-1 inline-flex items-center gap-1"
        style={{ background: tone.bg, color: tone.text }}
      >
        {pricing_label}
      </span>
      <span
        className="px-2 py-1 inline-flex items-center gap-0.5 font-mono-data"
        style={{ background: pctBg, color: pctColor }}
      >
        <Icon size={11} weight="bold" />
        {pctLabel}
        {has_partial && <sup className="ml-0.5 opacity-70">*</sup>}
      </span>
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────
// ReceivableSummary — visible cuando se filtra "Por cobrar".
// Muestra: total por cobrar, # de notas, # de clientes únicos, vencidas.
// ───────────────────────────────────────────────────────────────────
function ReceivableSummary({ sales }) {
  const stats = sales.reduce(
    (acc, s) => {
      const ccy = s.currency || "MXN";
      acc.byCurrency[ccy] = (acc.byCurrency[ccy] || 0) + Number(s.outstanding || 0);
      acc.clients.add(s.client_id || s.client_snapshot?.id);
      acc.notes += 1;
      if (s.display_status === "overdue") acc.overdue += 1;
      acc.totalPaid += Number(s.paid_total || 0);
      return acc;
    },
    { byCurrency: {}, clients: new Set(), notes: 0, overdue: 0, totalPaid: 0 },
  );
  const currencyEntries = Object.entries(stats.byCurrency).filter(([, v]) => v > 0);
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-gradient-to-br from-red-50 to-amber-50 border border-red-200 rounded"
      data-testid="receivable-summary"
    >
      <div>
        <div className="text-[10px] uppercase tracking-wider text-red-700 font-mono-data">Total por cobrar</div>
        <div className="font-heading text-2xl font-extrabold text-red-700 numeric" data-testid="receivable-total">
          {currencyEntries.length === 0
            ? "—"
            : currencyEntries.map(([ccy, amt]) => fmt(amt, ccy)).join(" + ")}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono-data">Notas con saldo</div>
        <div className="font-heading text-2xl font-extrabold text-zinc-900 numeric">{stats.notes}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono-data">Clientes únicos</div>
        <div className="font-heading text-2xl font-extrabold text-zinc-900 numeric">{stats.clients.size}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-amber-700 font-mono-data">Vencidas</div>
        <div className="font-heading text-2xl font-extrabold text-amber-700 numeric">{stats.overdue}</div>
      </div>
    </div>
  );
}

// ─────────────────────── Repair intake dialog (customer device) ───────────
const REPAIR_TYPES = [
  { key: "laptop", label: "Laptop", subtypes: null, baseFields: ["processor", "ram", "storage"], extras: [] },
  { key: "cpu", label: "CPU / PC de escritorio",
    subtypes: [
      { key: "torre", label: "Torre" }, { key: "mini", label: "Mini PC" },
      { key: "sff", label: "SFF" }, { key: "servidor", label: "Servidor" }],
    baseFields: ["processor", "ram", "storage"],
    extras: [
      { key: "gpu", label: "Tarjeta gráfica", placeholder: "ej. RTX 3060" },
      { key: "power_supply", label: "Fuente de poder", placeholder: "ej. 650W" },
    ],
  },
  { key: "phone", label: "Celular", subtypes: null, baseFields: ["storage"],
    extras: [
      { key: "imei", label: "IMEI", placeholder: "15 dígitos" },
      { key: "color", label: "Color" },
      { key: "battery_health", label: "Salud batería (%)" },
    ],
  },
  { key: "tablet", label: "Tableta", subtypes: null, baseFields: ["storage"],
    extras: [
      { key: "color", label: "Color" },
      { key: "screen_size", label: "Tamaño pantalla" },
    ],
  },
  { key: "monitor", label: "Monitor", subtypes: null, baseFields: [],
    extras: [
      { key: "screen_size", label: "Tamaño", placeholder: "ej. 27\"" },
      { key: "resolution", label: "Resolución", placeholder: "ej. 2560x1440" },
      { key: "panel", label: "Panel", placeholder: "IPS / VA / OLED" },
    ],
  },
  { key: "gadget", label: "Gadget / accesorio",
    subtypes: [
      { key: "camara", label: "Cámara" }, { key: "gopro", label: "GoPro" },
      { key: "audifonos", label: "Audífonos" }, { key: "webcam", label: "Webcam" },
      { key: "drone", label: "Drone" }, { key: "otro", label: "Otro" }],
    baseFields: [], extras: [],
  },
];
const REPAIR_TYPE_MAP = Object.fromEntries(REPAIR_TYPES.map((t) => [t.key, t]));

function RepairIntakeDialog({ open, onOpenChange, clients = [], onClientsChanged, onCreated }) {
  const empty = {
    client_id: "",
    customer_name: "", customer_phone: "",
    equipment_type: "laptop", equipment_subtype: "",
    brand: "", model: "",
    processor: "", ram: "", storage: "",
    fault_description: "", priority: "normal",
    location: "",
    includes_charger: false, includes_battery: false,
    specs: {},
    estimated_cost: "",
    assign_technician_id: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", phone: "", business_name: "" });
  const [savingClient, setSavingClient] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/hospital/technicians").then((r) => setTechnicians(r.data || [])).catch(() => setTechnicians([]));
  }, [open]);
  const reset = () => { setForm(empty); setCreatingNew(false); setNewClient({ name: "", phone: "", business_name: "" }); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSpec = (k, v) => setForm((f) => ({ ...f, specs: { ...f.specs, [k]: v } }));

  const pickedClient = clients.find((c) => c.id === form.client_id);

  const pickExisting = (c) => {
    setForm((f) => ({
      ...f,
      client_id: c.id,
      customer_name: c.display_name || c.name || "",
      customer_phone: c.phone || "",
    }));
    setPickerOpen(false);
  };

  const clearClient = () => {
    setForm((f) => ({ ...f, client_id: "", customer_name: "", customer_phone: "" }));
  };

  const submitNewClient = async () => {
    if (!newClient.name.trim()) { toast.error("Nombre del cliente"); return; }
    setSavingClient(true);
    try {
      const { data } = await api.post("/clients", {
        name: newClient.name.trim(),
        business_name: newClient.business_name.trim() || null,
        display_name: newClient.name.trim(),
        phone: newClient.phone.trim() || null,
        currency: "MXN",
        client_type: "final",
        crm_stage: "lead",
      });
      onClientsChanged?.((prev) => [data, ...prev]);
      pickExisting(data);
      setCreatingNew(false);
      setNewClient({ name: "", phone: "", business_name: "" });
      toast.success("Cliente creado y seleccionado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear el cliente");
    } finally {
      setSavingClient(false);
    }
  };

  const cur = REPAIR_TYPE_MAP[form.equipment_type] || REPAIR_TYPE_MAP.laptop;
  const changeType = (v) => setForm((f) => ({
    ...f, equipment_type: v, equipment_subtype: "", specs: {},
    processor: REPAIR_TYPE_MAP[v]?.baseFields.includes("processor") ? f.processor : "",
    ram: REPAIR_TYPE_MAP[v]?.baseFields.includes("ram") ? f.ram : "",
    storage: REPAIR_TYPE_MAP[v]?.baseFields.includes("storage") ? f.storage : "",
  }));

  const submit = async () => {
    if (!form.brand.trim() || !form.model.trim()) return toast.error("Marca y modelo son obligatorios");
    if (!form.fault_description.trim()) return toast.error("Describe la falla");
    if (!form.customer_name.trim()) return toast.error("Selecciona o registra un cliente");
    setSaving(true);
    try {
      const client_device = {
        equipment_type: form.equipment_type,
        equipment_subtype: form.equipment_subtype || null,
        brand: form.brand.trim(),
        model: form.model.trim(),
        processor: form.processor || null,
        ram: form.ram || null,
        storage: form.storage || null,
        includes_charger: form.includes_charger,
        includes_battery: form.includes_battery,
        specs: form.specs,
        client_id: form.client_id || null,
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim() || null,
      };
      await api.post("/hospital/tickets", {
        product_id: null,
        client_device,
        reason: `Nota de reparación · ${form.customer_name.trim()}`,
        fault_description: form.fault_description.trim(),
        priority: form.priority,
        location: form.location.trim() || null,
        source: "cliente",
        requesting_area: "Ventas",
        estimated_cost: form.estimated_cost === "" ? 0 : Number(form.estimated_cost) || 0,
        assign_technician_id: form.assign_technician_id || null,
      });
      toast.success("Nota de reparación enviada · Nota de venta creada");
      onCreated?.();
      close(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear la nota de reparación");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="repair-intake-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FirstAidKit size={20} weight="fill" className="text-[#E0000F]" />
            Nota de reparación · Equipo del cliente
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border border-zinc-200 rounded-md p-3 bg-zinc-50">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Cliente</div>
              {!creatingNew && (
                <button
                  type="button"
                  onClick={() => setCreatingNew(true)}
                  className="text-[10px] uppercase tracking-widest text-[#002FA7] hover:text-[#0043c2] inline-flex items-center gap-1"
                  data-testid="repair-new-client-btn"
                >
                  <Plus size={11} weight="bold" /> Registrar nuevo cliente
                </button>
              )}
            </div>

            {creatingNew ? (
              <div className="space-y-2 border border-[#002FA7]/30 bg-white p-2 rounded-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Nombre *</Label>
                    <Input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} className="mt-1 h-8 text-sm" data-testid="repair-newclient-name" />
                  </div>
                  <div>
                    <Label className="text-xs">Teléfono</Label>
                    <Input value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} className="mt-1 h-8 text-sm" placeholder="55 1234 5678" data-testid="repair-newclient-phone" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Negocio (opcional)</Label>
                  <Input value={newClient.business_name} onChange={(e) => setNewClient({ ...newClient, business_name: e.target.value })} className="mt-1 h-8 text-sm" data-testid="repair-newclient-business" />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="outline" onClick={() => setCreatingNew(false)} disabled={savingClient} className="h-7 text-xs">Cancelar</Button>
                  <Button size="sm" onClick={submitNewClient} disabled={savingClient || !newClient.name.trim()} className="h-7 text-xs bg-[#002FA7] hover:bg-[#0043c2]" data-testid="repair-newclient-save">
                    {savingClient ? "Guardando…" : "Guardar y seleccionar"}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      role="combobox"
                      aria-expanded={pickerOpen}
                      data-testid="repair-client-picker"
                      className="w-full flex items-center justify-between border border-zinc-300 rounded-md bg-white px-3 py-2 text-sm hover:border-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
                    >
                      <span className={pickedClient ? "" : "text-zinc-400"}>
                        {pickedClient
                          ? `${pickedClient.display_name || pickedClient.name}${pickedClient.business_name ? ` · ${pickedClient.business_name}` : ""}`
                          : "Buscar cliente por nombre, teléfono, ciudad…"}
                      </span>
                      <MagnifyingGlass size={14} className="text-zinc-400 ml-2 shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                    <Command shouldFilter>
                      <CommandInput placeholder="Buscar…" data-testid="repair-client-search" />
                      <CommandList>
                        <CommandEmpty>
                          Sin resultados. <button type="button" onClick={() => { setPickerOpen(false); setCreatingNew(true); }} className="text-[#002FA7] font-semibold underline">Registrar nuevo</button>
                        </CommandEmpty>
                        <CommandGroup>
                          {clients.map((c) => {
                            const label = `${c.display_name || c.name}${c.business_name ? ` · ${c.business_name}` : ""}`;
                            const searchable = [c.display_name, c.name, c.business_name, c.city, c.phone, c.email].filter(Boolean).join(" ").toLowerCase();
                            return (
                              <CommandItem
                                key={c.id}
                                value={searchable}
                                onSelect={() => pickExisting(c)}
                                className="cursor-pointer"
                                data-testid={`repair-client-option-${c.id}`}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{label}</span>
                                  <span className="text-[10px] text-zinc-500">
                                    {[c.city, c.phone].filter(Boolean).join(" · ")}
                                  </span>
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                {pickedClient && (
                  <div className="mt-2 flex items-center justify-between gap-2 text-xs text-zinc-600">
                    <div>
                      <span className="font-semibold">{pickedClient.display_name || pickedClient.name}</span>
                      {pickedClient.phone && <span className="ml-2 font-mono-data">{pickedClient.phone}</span>}
                    </div>
                    <button type="button" onClick={clearClient} className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#E0000F]">
                      Quitar
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="border border-zinc-200 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Categoría</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tipo *</Label>
                <Select value={form.equipment_type} onValueChange={changeType}>
                  <SelectTrigger className="mt-1" data-testid="repair-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REPAIR_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {cur.subtypes && (
                <div>
                  <Label className="text-xs">Subtipo</Label>
                  <Select value={form.equipment_subtype || "none"} onValueChange={(v) => set("equipment_subtype", v === "none" ? "" : v)}>
                    <SelectTrigger className="mt-1" data-testid="repair-subtype"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin especificar</SelectItem>
                      {cur.subtypes.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          <div className="border border-zinc-200 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Datos del equipo</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Marca *</Label>
                <Input value={form.brand} onChange={(e) => set("brand", e.target.value)} className="mt-1" data-testid="repair-brand" />
              </div>
              <div>
                <Label className="text-xs">Modelo *</Label>
                <Input value={form.model} onChange={(e) => set("model", e.target.value)} className="mt-1" data-testid="repair-model" />
              </div>
              {cur.baseFields.includes("processor") && (
                <div><Label className="text-xs">Procesador</Label><Input value={form.processor} onChange={(e) => set("processor", e.target.value)} className="mt-1" data-testid="repair-cpu" /></div>
              )}
              {cur.baseFields.includes("ram") && (
                <div><Label className="text-xs">RAM</Label><Input value={form.ram} onChange={(e) => set("ram", e.target.value)} className="mt-1" data-testid="repair-ram" /></div>
              )}
              {cur.baseFields.includes("storage") && (
                <div><Label className="text-xs">Almacenamiento</Label><Input value={form.storage} onChange={(e) => set("storage", e.target.value)} className="mt-1" data-testid="repair-storage" /></div>
              )}
              {cur.extras.map((ex) => (
                <div key={ex.key}>
                  <Label className="text-xs">{ex.label}</Label>
                  <Input value={form.specs[ex.key] || ""} onChange={(e) => setSpec(ex.key, e.target.value)} placeholder={ex.placeholder} className="mt-1" data-testid={`repair-spec-${ex.key}`} />
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-3 flex-wrap">
              <label className="inline-flex items-center gap-2 text-sm"><Checkbox checked={form.includes_charger} onCheckedChange={(v) => set("includes_charger", !!v)} data-testid="repair-charger" /> Incluye cargador</label>
              <label className="inline-flex items-center gap-2 text-sm"><Checkbox checked={form.includes_battery} onCheckedChange={(v) => set("includes_battery", !!v)} data-testid="repair-battery" /> Incluye batería</label>
            </div>
          </div>

          <div className="border border-zinc-200 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Falla reportada</div>
            <Textarea rows={3} value={form.fault_description} onChange={(e) => set("fault_description", e.target.value)} placeholder="No enciende, teclado falla, pantalla muerta…" data-testid="repair-fault" />
            <div className="grid grid-cols-3 gap-3 mt-3">
              <div>
                <Label className="text-xs">Ubicación física</Label>
                <Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Mesa 2, Estante B…" className="mt-1" data-testid="repair-location" />
              </div>
              <div>
                <Label className="text-xs">Prioridad</Label>
                <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                  <SelectTrigger className="mt-1" data-testid="repair-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">🔴 Urgente</SelectItem>
                    <SelectItem value="high">🟠 Alta</SelectItem>
                    <SelectItem value="normal">🔵 Normal</SelectItem>
                    <SelectItem value="low">⚪ Baja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Costo estimado (opcional)</Label>
                <div className="relative mt-1">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-zinc-400 font-mono-data">MX$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.estimated_cost}
                    onChange={(e) => set("estimated_cost", e.target.value)}
                    placeholder="0.00"
                    className="pl-10 numeric"
                    data-testid="repair-estimated-cost"
                  />
                </div>
                <div className="text-[10px] text-zinc-400 mt-1">Puedes dejarlo en 0 y cotizar después</div>
              </div>
            </div>
            <div className="mt-3 border-t border-zinc-100 pt-3">
              <Label className="text-xs inline-flex items-center gap-1.5">
                <User size={12} weight="bold" /> Asignar técnico (opcional)
              </Label>
              <Select
                value={form.assign_technician_id || "__none"}
                onValueChange={(v) => set("assign_technician_id", v === "__none" ? "" : v)}
              >
                <SelectTrigger className="mt-1" data-testid="repair-assign-tech">
                  <SelectValue placeholder="Sin asignar — cualquier técnico podrá tomarla" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Sin asignar — libre para tomar</SelectItem>
                  {technicians.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`repair-tech-${t.id}`}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-zinc-400 mt-1">
                Si asignas, el ticket entra directo a <b>En diagnóstico</b> en el tablero del técnico y le sonará una alerta.
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => close(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#E0000F] hover:bg-[#c30414] text-white gap-1.5" data-testid="repair-submit">
              <FirstAidKit size={14} weight="bold" />
              {saving ? "Enviando…" : "Enviar al taller"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

